/**
 * speciesHelpers.js
 *
 * Pure species-related helpers shared by App.js and unit tests.
 */

const normalizeSpeciesText = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

/**
 * 這些模式的物種是從 available_species.config.json 挑出來的完整條目
 * （俗名 / 學名 / 參考基因組），UI 要顯示下拉選單加詳細資訊表格；
 * 其餘模式只是單純的字串下拉選單。
 */
export const DETAILED_SPECIES_MODES = ['bulk', 'scrna', 'visiumhd', 'chipseq'];

export const isDetailedSpeciesMode = (mode = '') => DETAILED_SPECIES_MODES.includes(mode);

export const getSpeciesSelectionKey = (species = {}) => JSON.stringify({
  commonName: normalizeSpeciesText(species.commonName),
  scientificName: normalizeSpeciesText(species.scientificName),
  genome: normalizeSpeciesText(species.genome)
});

export const parseSpeciesSelectionKey = (selectionKey = '') => {
  const text = normalizeSpeciesText(selectionKey);
  if (!text) return null;

  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    return {
      commonName: normalizeSpeciesText(parsed.commonName),
      scientificName: normalizeSpeciesText(parsed.scientificName),
      genome: normalizeSpeciesText(parsed.genome)
    };
  } catch {
    return null;
  }
};

export const getSpeciesOptionLabel = (species = {}) => {
  const commonName = normalizeSpeciesText(species.commonName);
  const genome = normalizeSpeciesText(species.genome);

  if (!commonName && !genome) return '';
  if (!genome) return commonName;
  if (!commonName) return genome;
  return `${commonName} (${genome})`;
};

export const findSpeciesOptionByFields = (
  speciesCommon = '',
  speciesScientific = '',
  speciesGenome = '',
  options = []
) => {
  const targetCommon = normalizeSpeciesText(speciesCommon).toLowerCase();
  const targetScientific = normalizeSpeciesText(speciesScientific).toLowerCase();
  const targetGenome = normalizeSpeciesText(speciesGenome).toLowerCase();

  if (!targetCommon && !targetScientific && !targetGenome) {
    return null;
  }

  return (Array.isArray(options) ? options : []).find(option =>
    normalizeSpeciesText(option?.commonName).toLowerCase() === targetCommon &&
    normalizeSpeciesText(option?.scientificName).toLowerCase() === targetScientific &&
    normalizeSpeciesText(option?.genome).toLowerCase() === targetGenome
  ) || null;
};

export const findSpeciesOptionBySelection = (selection = '', options = []) => {
  const selectionText = normalizeSpeciesText(selection);
  if (!selectionText || selectionText === '物種請選擇' || selectionText === '其他') {
    return null;
  }

  const parsedSelection = parseSpeciesSelectionKey(selectionText);
  if (parsedSelection) {
    const byKey = findSpeciesOptionByFields(
      parsedSelection.commonName,
      parsedSelection.scientificName,
      parsedSelection.genome,
      options
    );

    if (byKey) {
      return byKey;
    }
  }

  return (Array.isArray(options) ? options : []).find(option =>
    normalizeSpeciesText(option?.commonName) === selectionText
  ) || null;
};

export const createEmptySpeciesState = () => ({
  speciesSelection: '物種請選擇',
  speciesSelectionKey: '',
  speciesCommon: '',
  speciesScientific: '',
  speciesGenome: '',
  speciesOther: '',
  TenXGenomicsRefVersion: '',
  TenXGenomicsProbeSetVersion: '',
  TenXGenomicsVDJVersion: ''
});

export const createSpeciesHelpers = ({
  isBulkRNAseqService,
  isScRNAseqService,
  isVisiumHDService,
  isChIPseqService,
  isSingleronService,
  availableSpecies,
  serviceCatalog
} = {}) => {
  const bulkDetector = typeof isBulkRNAseqService === 'function'
    ? isBulkRNAseqService
    : () => false;
  const scDetector = typeof isScRNAseqService === 'function'
    ? isScRNAseqService
    : () => false;
  const visiumHDDetector = typeof isVisiumHDService === 'function'
    ? isVisiumHDService
    : () => false;
  const chipSeqDetector = typeof isChIPseqService === 'function'
    ? isChIPseqService
    : () => false;
  const singleronDetector = typeof isSingleronService === 'function'
    ? isSingleronService
    : () => false;
  const speciesData = availableSpecies || {};

  const isHumanOrMouse = (species = {}) => species.commonName === 'Human' || species.commonName === 'Mouse';

  // 這些實驗類型只支援 Human / Mouse：SC04-SC06 受 10X reference 限制，SC09 為 Singleron 平台。
  const HUMAN_MOUSE_ONLY_EXPERIMENT_CODES = ['SC04', 'SC05', 'SC06', 'SC09'];

  const hasRestrictedScRnaExperiment = (context = {}) => {
    const serviceName = String(context?.scRNAExperimentInfo?.serviceName || '').trim();
    const code = (serviceName.match(/^(SC\d+)/i) || [])[1];
    return Boolean(code) && HUMAN_MOUSE_ONLY_EXPERIMENT_CODES.includes(code.toUpperCase());
  };

  const getSpeciesMode = (serviceItems = []) => {
    const items = Array.isArray(serviceItems) ? serviceItems : [];

    const hasBulkRNAseq = items.some(item =>
      item?.services?.some(svc => bulkDetector(svc.service))
    );
    if (hasBulkRNAseq) return 'bulk';

    const hasScRNAseq = items.some(item =>
      item?.services?.some(svc => scDetector(svc.service))
    );
    if (hasScRNAseq) return 'scrna';

    const hasVisiumHD = items.some(item =>
      item?.services?.some(svc => visiumHDDetector(svc.service))
    );
    if (hasVisiumHD) return 'visiumhd';

    const hasChIPseq = items.some(item =>
      item?.services?.some(svc => chipSeqDetector(svc.service))
    );
    if (hasChIPseq) return 'chipseq';

    return 'legacy';
  };

  const canSelectOtherSpecies = (mode, serviceItems = [], context = {}) => {
    if (mode === 'visiumhd') return false;
    if (mode === 'scrna' && hasRestrictedScRnaExperiment(context)) return false;
    return true;
  };

  const getSpeciesOptionsForMode = (mode, serviceItems = [], context = {}) => {
    let options = [];
    if (mode === 'bulk') options = Array.isArray(speciesData.isRNAseqService) ? speciesData.isRNAseqService : [];
    else if (mode === 'scrna') options = Array.isArray(speciesData.isScRNAseqService) ? speciesData.isScRNAseqService : [];
    else if (mode === 'visiumhd') options = Array.isArray(speciesData.isVisiumHDService) ? speciesData.isVisiumHDService : [];
    // ChIP-seq 的物種和 RNA-seq 相同，共用同一份清單，設定檔不用多開一個 key。
    else if (mode === 'chipseq') options = Array.isArray(speciesData.isRNAseqService) ? speciesData.isRNAseqService : [];

    // Singleron 限制：若包含 Singleron 服務，則 scRNA 模式下僅顯示 Human/Mouse/其他
    const items = Array.isArray(serviceItems) ? serviceItems : [];
    const hasSingleron = items.some(item =>
      item?.services?.some(svc => singleronDetector(svc.service))
    );

    if ((hasSingleron || hasRestrictedScRnaExperiment(context)) && mode === 'scrna') {
      return options.filter(isHumanOrMouse);
    }

    if (mode === 'visiumhd') {
      return options.filter(isHumanOrMouse);
    }

    return options;
  };

  const normalizeSpeciesFields = (data = {}) => {
    const mode = getSpeciesMode(data.serviceItems || []);
    const selection = data.speciesSelection || '物種請選擇';
    const selectionKey = normalizeSpeciesText(data.speciesSelectionKey);
    const base = {
      ...data,
      ...createEmptySpeciesState(),
      speciesSelection: selection,
      speciesSelectionKey: selectionKey
    };

    if (selection === '物種請選擇') {
      return base;
    }

    if (isDetailedSpeciesMode(mode)) {
      if (selection === '其他') {
        if (!canSelectOtherSpecies(mode, data.serviceItems || [], data)) {
          return {
            ...base,
            speciesSelection: '物種請選擇'
          };
        }

        const normalized = {
          ...base,
          speciesSelectionKey: '其他',
          speciesCommon: data.speciesCommon || data.speciesOther || '',
          speciesScientific: data.speciesScientific || '',
          speciesGenome: data.speciesGenome || ''
        };

        if (mode === 'scrna' || mode === 'visiumhd') {
          normalized.TenXGenomicsRefVersion = data.TenXGenomicsRefVersion || '';
          normalized.TenXGenomicsProbeSetVersion = data.TenXGenomicsProbeSetVersion || '';
          normalized.TenXGenomicsVDJVersion = data.TenXGenomicsVDJVersion || '';
        }

        return normalized;
      }

      const speciesOptions = getSpeciesOptionsForMode(mode, data.serviceItems || [], data);
      const found = findSpeciesOptionBySelection(selectionKey, speciesOptions)
        || findSpeciesOptionByFields(data.speciesCommon, data.speciesScientific, data.speciesGenome, speciesOptions)
        || findSpeciesOptionBySelection(selection, speciesOptions);

      if (!found && !canSelectOtherSpecies(mode, data.serviceItems || [], data)) {
        return {
          ...base,
          speciesSelection: '物種請選擇'
        };
      }

      return {
        ...base,
        speciesSelection: found?.commonName || selection,
        speciesSelectionKey: found ? getSpeciesSelectionKey(found) : (selectionKey || selection),
        speciesCommon: found?.commonName || data.speciesCommon || selection,
        speciesScientific: found?.scientificName || data.speciesScientific || '',
        speciesGenome: found?.genome || data.speciesGenome || '',
        speciesOther: '',
        TenXGenomicsRefVersion: (mode === 'scrna' || mode === 'visiumhd') ? (found?.TenXGenomicsRefVersion || data.TenXGenomicsRefVersion || '') : '',
        TenXGenomicsProbeSetVersion: (mode === 'scrna' || mode === 'visiumhd') ? (found?.TenXGenomicsProbeSetVersion || data.TenXGenomicsProbeSetVersion || '') : '',
        TenXGenomicsVDJVersion: mode === 'scrna' ? (found?.TenXGenomicsVDJVersion || data.TenXGenomicsVDJVersion || '') : ''
      };
    }

    if (selection === '其他') {
      return {
        ...base,
        speciesSelectionKey: '其他',
        speciesCommon: '',
        speciesScientific: '',
        speciesGenome: '',
        speciesOther: data.speciesOther || data.speciesCommon || ''
      };
    }

    return {
      ...base,
      speciesSelectionKey: selection,
      speciesCommon: selection,
      speciesScientific: '',
      speciesGenome: '',
      speciesOther: ''
    };
  };

  const getSpeciesDisplayInfo = (data = {}) => {
    const mode = getSpeciesMode(data.serviceItems || []);
    const selection = data.speciesSelection || '物種請選擇';

    if (selection === '物種請選擇') {
      return {
        primary: '未填寫',
        details: [],
        tenx: []
      };
    }

    if (isDetailedSpeciesMode(mode)) {
      return {
        primary: selection === '其他'
          ? (data.speciesCommon || data.speciesOther || '未填寫')
          : (data.speciesCommon || selection || '未填寫'),
        details: [data.speciesScientific, data.speciesGenome].filter(Boolean),
        tenx: (mode === 'scrna' || mode === 'visiumhd')
          ? [
            data.TenXGenomicsRefVersion,
            data.TenXGenomicsProbeSetVersion,
            data.TenXGenomicsVDJVersion
          ].filter(Boolean)
          : []
      };
    }

    return {
      primary: selection === '其他'
        ? (data.speciesOther || data.speciesCommon || '未填寫')
        : (data.speciesCommon || selection || '未填寫'),
      details: [],
      tenx: []
    };
  };



  return {
    createEmptySpeciesState,
    isDetailedSpeciesMode,
    getSpeciesMode,
    getSpeciesOptionsForMode,
    canSelectOtherSpecies,
    normalizeSpeciesFields,
    getSpeciesDisplayInfo,
    getSpeciesOptionLabel,
    getSpeciesSelectionKey,
    findSpeciesOptionBySelection,
    findSpeciesOptionByFields
  };
};
